// ignore-string-externalization

import React from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { decycle, retrocycle } from 'cycle';
import { templateLoader, TemplateType, Template } from '../features/Template';
import { getLanguage } from '../utils/getLanguage';
import { useRouter } from 'next/router';
import { fetchLandingPageSlugs } from '../features/Contentful/utils/fetchContentSlugs';
import memoize from 'lodash/memoize';

type Props = {
  template: TemplateType;
};

const cacheKey = 1;

/**
 * Fetches all landing pages from Contentful, and returns their slugs.
 * Responses are cached in memory for a short amount of time.
 * The caching cannot be too long because we want newly published pages to show up quickly.
 */
const getLandingPageSlugs = memoize(fetchLandingPageSlugs, () => cacheKey);

// clear landing page slugs cache after a minute
setInterval(() => {
  getLandingPageSlugs.cache.delete(cacheKey);
  getLandingPageSlugs();
}, 6000);

/**
 * Fetches all landing pages to prerender them
 * @see https://nextjs.org/docs/pages/building-your-application/data-fetching/get-static-props
 */
export const getStaticProps: GetStaticProps<Props> = async ctx => {
  const landingPageSlugs = await getLandingPageSlugs();
  const urlSlug = [ctx.params?.urlSlug].flat().join('/');

  // preview pages are not yet published, so they can still be previewed
  if (!ctx.preview && !landingPageSlugs.includes(urlSlug)) {
    // throw NOT_FOUND error to force a refresh of the page so that next.config redirects work
    throw new Error('NOT_FOUND');
  }

  const template = await templateLoader.load({
    contentType: 'landingPage',
    urlSlug,
    smartlingLanguage: getLanguage(ctx.locale!),
    preview: ctx.preview,
  });

  // if template is falsy (null or undefined)
  // if there's no contentType
  // we assume no valid content to show
  // and thus cannot be used to render page
  if (!template?.contentType) {
    // triggers 500.tsx error page
    throw new Error('failed to load template');
  }

  return {
    props: {
      template: decycle(template),
    },
    // Next.js will attempt to re-generate the page:
    // - When a request comes in
    // - At most once every 60 seconds
    revalidate: 60,
  };
};

/**
 * Pre-generates critical landing pages at build time for optimal LCP.
 * Other pages are generated on-demand with ISR.
 * @see: https://nextjs.org/docs/pages/building-your-application/data-fetching/get-static-paths
 */
export const getStaticPaths: GetStaticPaths = () => {
  // Pre-generate /home at build time for best performance
  // Other pages will be generated on-demand with ISR (revalidate: 60)
  return {
    paths: [{ params: { urlSlug: ['home'] } }],
    // Use blocking fallback for reliability in production
    // Pre-generated pages (/home) will still be instant
    fallback: 'blocking',
  };
};

export const LandingPage: NextPage<Props> = ({ template }) => {
  const router = useRouter();

  return (
    <Template
      {...retrocycle(template)}
      canonicalUrl={`/${template.urlSlug}`.replace(/^\/home$/, '/')}
      preview={router.isPreview}
    />
  );
};

export default LandingPage;
